import React, { useEffect, memo } from "react";
import StepCustomize from "../../../../components/stepCustomize";
import { useDispatch, useSelector } from "react-redux";
import {
  updateBuilder,
  updateBuilderLinks,
} from "../../../../redux/slices/linksBuilder";
import AddLinkItem from "./AddLinkItem";
import SocialLinks from "./SocialLinks";
import QrTitleField from "../../../../components/input/qrTitleField";
import {
  handleFilesUpdate,
  isValidLinkString,
} from "../../../../hooks/helpers";
import { setIsStorageModal } from "../../../../redux/slices/modals";
import InputText from "../../../../components/input/inputText";
import InputTextarea from "../../../../components/input/inputTextarea";
import FileUpload from "../../../../components/input/fileUpload";
import Form from "react-bootstrap/Form";
import { Button } from "react-bootstrap";
import { SelectCategory } from "../../../../components";
import { navigationStep } from "../../../../redux/slices/navigation";
import { useNavigate } from "react-router-dom";
import Design from "./Design";
import { BackIcon, PlusIcon, ArrowIcon } from "../svg";
import { isNoEditRoute } from "../../../../hooks/helpers";
import classNames from "classnames";
import { validLinkPattern } from "../../../../RegExpValidations";
import usePremium from "../../../../hooks/usePremium";
import { registerPlugin } from "react-filepond";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";
import { useDebouncedCallback } from "use-debounce";
import useUserInfo from "../../../../hooks/useUserInfo";

registerPlugin(FilePondPluginFileValidateType);

export default memo(function LinkConfigurator({
  step,
  error,
  handleLinkValidate,
  logo,
  setLogo,
  isNoValidate,
}) {
  const { lang, optionalText, backText, generateQrText, configData } =
    useSelector((state) => ({
      lang: state.changeLang.language.stepTwo.linkList || [],
      optionalText: state.changeLang.language.stepTwo.optional,
      backText: state.changeLang.language.nav.desktop[0],
      generateQrText: state.changeLang.language.sliderHint.secondSlide.title,
      configData: state.linksBuilder.data,
    }));

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isPremium } = usePremium();
  const debouncedHandleFilesUpdate = useDebouncedCallback(
    handleFilesUpdate,
    500
  );
  const { isAdmin } = useUserInfo();

  const maxFileSize = isAdmin ? null : isPremium ? 99 : 10;

  useEffect(() => {
    const reader = new FileReader();
    if (logo[0]?.file) {
      reader.readAsDataURL(logo[0].file);
      reader.onload = () => {
        dispatch(updateBuilder({ ...configData, logo: reader.result }));
      };
    } else {
    }
  }, [logo]);

  const handleBack = () => {
    dispatch(navigationStep(1));
    navigate("/");
  };

  const handleNextStep = () => {
    window.document.querySelector("#secondStepClick")?.click();
    const scrollTo = (block) =>
      document
        .querySelector(block)
        .scrollIntoView({ behavior: "smooth", block: "center" });
    if (isNoValidate) {
      if (!configData.title.trim()) {
        scrollTo("#head");
      } else if (
        configData.links.some(
          (link) =>
            !link.text.trim() ||
            !link.url.trim() ||
            !isValidLinkString(link.url.trim())
        ) ||
        configData.socials.some(
          (social) =>
            !social.link.trim() || !validLinkPattern.test(social.link.trim())
        )
      ) {
        document.querySelector(".error-field")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "center",
        });
      }
    }
  };

  return (
    <>
      <section className="bg-white p-3 p-md-4 mt-3 mb-lg-4 rounded-1" id="head">
        <div className="fw-bold fs-4 mb-3 mb-md-4 text-capitalize ">
          {lang[26]}
        </div>
        <FileUpload
          className="mb-1 text-capitalize"
          files={logo}
          onupdatefiles={(files) =>
            debouncedHandleFilesUpdate(
              files,
              setLogo,
              () => dispatch(setIsStorageModal(true)),
              !isAdmin
            )
          }
          onremovefile={() => {
            setLogo([]);
            dispatch(updateBuilder({ ...configData, logo: null }));
          }}
          allowFileTypeValidation={true}
          maxFileSize={maxFileSize ? `${maxFileSize}MB` : null}
          allowMultiple={false}
          maxFiles={1}
          name="gallery"
          dataAllowReorder={true}
          allowReorder={true}
          acceptedFileTypes={["image/jpeg", "image/png"]}
          label={lang[5]}
          popoverText={null}
          showReplaceBtn
        />
        <div className="fs-7 text-gray mb-3 text-capitalize">{lang[29]}</div>

        <Form.Check
          className="custom-check mb-3"
          type="checkbox"
          label={lang[34]}
          id="logo-round"
          checked={configData.isLogoRound}
          onChange={() =>
            dispatch(
              updateBuilder({
                ...configData,
                isLogoRound: !configData.isLogoRound,
              })
            )
          }
        />

        <InputText
          type="text"
          placeholder={lang[6]}
          name="title"
          value={configData.title}
          onChange={(e) =>
            dispatch(updateBuilder({ ...configData, title: e.target.value }))
          }
          popoverText={null}
          valid={configData.title.length}
          error={error && !configData.title.length}
        >
          {error && !configData.title.trim() && (
            <Form.Text className="text-danger">{lang[8]}</Form.Text>
          )}
        </InputText>
        <InputTextarea
          className="mb-0"
          style={{ height: 100 }}
          placeholder={`${lang[9]} (${optionalText})`}
          name="description"
          value={configData.description}
          onChange={(e) =>
            dispatch(
              updateBuilder({ ...configData, description: e.target.value })
            )
          }
          maxLength={100}
          popoverText={null}
        />
      </section>

      <section
        className="bg-white p-3 p-md-4 mt-3 mb-lg-4 rounded-1"
        id="links"
      >
        <div className="fw-bold fs-4 mb-3 mb-md-4 text-capitalize">
          {lang[11]}
        </div>
        <AddLinkItem error={error} handleLinkValidate={handleLinkValidate} />

        {configData.links.length < 20 && (
          <Button
            variant="outline-primary"
            className="fs-7 d-flex align-items-center justify-content-center w-100 p-2 text-capitalize text-nowrap w-auto"
            onClick={() =>
              dispatch(
                updateBuilderLinks([
                  ...configData.links,
                  {
                    id: `linkButton-${Date.now()}`,
                    url: "",
                    text: "",
                    img: "",
                  },
                ])
              )
            }
          >
            <span>
              <PlusIcon className="me-2" /> {lang[12]}
            </span>
          </Button>
        )}
      </section>

      <section
        className="bg-white p-3 p-md-4 mt-3 mb-lg-4 rounded-1"
        id="social"
      >
        <div className="fw-bold fs-4 mb-3 mb-md-4 text-capitalize">
          {lang[21]}
        </div>
        <SocialLinks error={error} handleLinkValidate={handleLinkValidate} />
      </section>

      <section
        className="bg-white p-3 p-md-4 mt-3 mb-lg-4 text-capitalize rounded-1"
        id="design"
      >
        <Design />
      </section>

      <section
        className="bg-white p-3 p-md-4 mt-3 mb-lg-4 text-capitalize rounded-1"
        id="info"
      >
        <div className="fw-bold fs-4 mb-3 mb-md-4">{lang[33]}</div>
        <QrTitleField
          value={configData.qrTitle}
          onChange={(e) =>
            dispatch(updateBuilder({ ...configData, qrTitle: e.target.value }))
          }
          popoverText={null}
        />
        <SelectCategory className="col-lg-12" popoverText={null} />
      </section>

      <section className="bg-white p-3 p-md-4 mt-3 text-capitalize rounded-1">
        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="outline-primary"
            className={classNames("text-capitalize fs-7 text-nowrap col-2", {
              "d-none": !isNoEditRoute,
            })}
            onClick={handleBack}
          >
            <ArrowIcon /> <span className="d-none d-md-inline">{backText}</span>
          </Button>
          <Button
            id="linkListClick"
            variant="primary"
            className="text-capitalize text-bold text-nowrap col-4"
            onClick={handleNextStep}
          >
            <span className="d-none d-md-inline">
              {isNoEditRoute ? generateQrText : "Save"}
            </span>{" "}
            <BackIcon />
          </Button>
          <div className="d-none">
            <StepCustomize emulate={step} />
          </div>
        </div>
      </section>
    </>
  );
});
